import { Fragment, useMemo, type RefObject } from 'react';
import { useWholeRowPaging } from '../../hooks/useWholeRowPaging';
import {
  genderHeading,
  presentColumn,
  type BigScreenLine,
  type BigScreenShooter,
  type BigScreenTheme,
} from '../../lib/big-screen';
import type { BigScreenQueue } from '../../lib/big-screen-queue';
import type { PublicLeaderboard } from '../../lib/shooting';
import { ShootingQueuePanel } from './ShootingQueuePanel';

export type BigScreenBoardView = 'round1' | 'round2';

export interface BigScreenStageProps {
  theme: BigScreenTheme;
  isAdmin: boolean;
  missingGender: number;
  themeError?: string | null;
  themeSaving?: boolean;
  onTheme?: (theme: BigScreenTheme) => void;
  men: BigScreenShooter[];
  women: BigScreenShooter[];
  menCut: number;
  womenCut: number;
  /** Hold the last scroll page (the men's cut line) instead of paging. */
  holdEnd?: boolean;
  queue: BigScreenQueue | null;
  clock: string;
  live: boolean;
  rifle: boolean;
  boardView?: BigScreenBoardView;
  onBoardView?: (view: BigScreenBoardView) => void;
  round1?: PublicLeaderboard['round1'] | null;
  round2Board?: PublicLeaderboard['round2'] | null;
}

export function BigScreenStage(props: BigScreenStageProps) {
  const light = props.theme === 'light';
  const round2 = props.boardView === 'round2';
  const finalists = useMemo(() => {
    const members = (props.round2Board?.teams ?? []).flatMap((team) =>
      team.members.map((member) => ({ ...member, teamName: team.name })),
    );
    const column = (gender: 'male' | 'female'): BigScreenShooter[] => {
      const sorted = members.filter((member) => member.gender === gender && member.shotsFired > 0)
        .sort((a, b) => Number(b.totalDisplay) - Number(a.totalDisplay));
      let rank = 0;
      return sorted.map((member, index) => {
        // The public Round 2 payload has totals, not shot-by-shot tie breakers.
        // Equal totals share a display rank; never substitute Round 1 ranks.
        if (index === 0 || Number(member.totalDisplay) !== Number(sorted[index - 1].totalDisplay)) rank = index + 1;
        return { ...member, rank, position: index + 1, score: member.totalDisplay, top: index < 3 };
      });
    };
    return { male: column('male'), female: column('female') };
  }, [props.round2Board]);
  return (
    <div className={`bigscreen ${props.theme}`} data-theme={props.theme}>
      <header>
        <img className="logo" src="/assets/scoreboard/smym-logo.png?v=20261103" alt="SMYM" />
        <div className="org">
          <b>SHREE MAHESHWARI YUVA MANDAL, CHENNAI</b>
          <span>Presents</span>
        </div>
        {props.rifle ? (
          <img className="t109" src="/assets/scoreboard/rifle-109.png" alt="10.9 Rifle Shooting Tournament" />
        ) : (
          <h1 className="event-title">Live scores</h1>
        )}
        <div className="round">{round2 ? 'Round 2' : 'Round 1'}</div>
        {props.isAdmin && props.rifle && props.onBoardView ? (
          <nav className="view-switch" aria-label="Round">
            {(
              [
                ['round1', 'Round 1'],
                ['round2', 'Round 2'],
              ] as const
            ).map(([value, label]) => (
              <button key={value} type="button" aria-pressed={(props.boardView ?? 'round1') === value} onClick={() => props.onBoardView?.(value)}>
                {label}
              </button>
            ))}
          </nav>
        ) : null}
        <div className="live-wrap">
          {props.isAdmin ? (
            <ThemeSwitch theme={props.theme} saving={props.themeSaving} onTheme={props.onTheme} />
          ) : null}
          <div className="live">
            <div className={props.live ? 'dot' : 'dot idle'} />
            <small>LIVE</small>
            <span className="clock">{props.clock}</span>
          </div>
        </div>
      </header>
      <main className={`${props.rifle ? 'combined' : ''} ${props.queue ? 'with-queue' : 'without-queue'}`}>
        <section className="boards">
          <GenderColumn title={genderHeading('male')} rows={round2 ? finalists.male : props.men} cut={round2 ? 0 : props.menCut} holdEnd={props.holdEnd} showTeam={props.rifle} startingSoon={round2 && !props.round2Board} />
          <GenderColumn title={genderHeading('female')} rows={round2 ? finalists.female : props.women} cut={round2 ? 0 : props.womenCut} holdEnd={props.holdEnd} showTeam={props.rifle} startingSoon={round2 && !props.round2Board} />
        </section>
        {props.rifle ? (
          <section className="team-area" aria-label="Team leaderboard">
            <TeamBoard round1={round2 ? null : props.round1} round2={round2 ? props.round2Board : null} isRound2={round2} holdEnd={props.holdEnd} />
          </section>
        ) : null}
        {props.queue ? props.rifle ? (
          <>
            <ShootingQueuePanel queue={props.queue} part="now" />
            <ShootingQueuePanel queue={props.queue} part="next" />
          </>
        ) : <ShootingQueuePanel queue={props.queue} /> : null}
      </main>
      {props.isAdmin && (props.missingGender > 0 || props.themeError) ? (
        <p className="missing">
          {props.themeError
            ? props.themeError
            : `${props.missingGender} shooter${props.missingGender === 1 ? '' : 's'} with no Male or Female left off this screen.`}
        </p>
      ) : null}
      <span className="sr-only">{light ? 'Light theme' : 'Dark theme'}</span>
    </div>
  );
}

function ThemeSwitch({
  theme,
  saving,
  onTheme,
}: {
  theme: BigScreenTheme;
  saving?: boolean;
  onTheme?: (theme: BigScreenTheme) => void;
}) {
  const light = theme === 'light';
  return (
    <div className="theme-toggle">
      <span className="theme-word">Dark</span>
      <button
        type="button"
        role="switch"
        aria-checked={light}
        aria-label="Light theme"
        disabled={saving || !onTheme}
        className={light ? 'on' : ''}
        onClick={() => onTheme?.(light ? 'dark' : 'light')}
      >
        <span />
      </button>
      <span className="theme-word">Light</span>
    </div>
  );
}

function GenderColumn({
  title,
  rows,
  cut,
  holdEnd,
  showTeam,
  startingSoon,
}: {
  title: string;
  rows: BigScreenShooter[];
  cut: number;
  holdEnd?: boolean;
  showTeam?: boolean;
  startingSoon?: boolean;
}) {
  const plan = useMemo(() => presentColumn(rows, cut), [rows, cut]);
  const paging = useWholeRowPaging(`${cut}:${rows.map(row => row.shooterId).join(',')}`, holdEnd);

  return (
    <div className="board" aria-label={title}>
      <div className="bh">
        <h2>{title}</h2>
        {plan.note?.variant === 'scroll' ? (
          <span className="tag">
            <i />
            {plan.note.text}
          </span>
        ) : plan.note ? (
          <span>{plan.note.text}</span>
        ) : null}
      </div>
      <Head showTeam={showTeam} />
      {rows.length === 0 ? <p className="empty">{startingSoon ? 'Round 2 starting soon' : 'Scores will show up here as they are entered.'}</p> : null}
      {plan.scrolling ? (
        <>
          <div className="pinned">
            <ShooterTable lines={plan.pinned} showTeam={showTeam} />
          </div>
          <div className="win" ref={paging.viewport} style={{ height: plan.windowPx }}>
            <div className="whole-row-clip" style={{ height: paging.page.height }}>
              <ShooterTable lines={plan.rest} offset={paging.page.offset} tableRef={paging.content} showTeam={showTeam} />
            </div>
          </div>
        </>
      ) : (
        <ShooterTable lines={plan.rest} showTeam={showTeam} />
      )}
    </div>
  );
}

function TeamBoard({ round1, round2, isRound2, holdEnd }: {
  round1?: PublicLeaderboard['round1'] | null;
  round2?: PublicLeaderboard['round2'] | null;
  isRound2: boolean;
  holdEnd?: boolean;
}) {
  const teams = isRound2 ? round2?.teams ?? [] : round1?.teams ?? [];
  const tied = new Set(round1?.tie?.teamIds ?? []);
  const tiedAt = teams.findIndex((team) => tied.has(team.teamId));
  const cutAt = isRound2 ? -1 : round1?.tie ? (tiedAt === -1 ? round1.qualifyingTeams : tiedAt) : round1?.qualifyingTeams ?? -1;
  const structure = `${isRound2}:${cutAt}:${teams.map(team => `${team.teamId}:${team.members.length}`).join(',')}`;
  const paging = useWholeRowPaging(structure, holdEnd);

  const teamRows = (start: number, end?: number) => teams.slice(start, end).map((team, index) => (
    <Fragment key={team.teamId}>
      {start + index === cutAt && cutAt > 0 ? <tr className="cutlabel"><td colSpan={3}>▲ Round 2 teams</td></tr> : null}
      <tr className={`${team.rank > 0 && team.rank <= 3 ? 'top' : ''} ${(start + index) % 2 === 0 ? 'odd' : ''}`}>
        <td className="rank">{team.rank || '—'}</td>
        <td title={team.name}>
          <span className="team-title">{team.name}</span>
        </td>
        <td className="score">{team.totalDisplay}</td>
      </tr>
    </Fragment>
  ));
  const cols = <colgroup><col className="c-rank" /><col /><col className="c-score" /></colgroup>;
  return (
      <div className={`board team-board ${isRound2 ? 'round2-teams' : ''}`}>
        <div className="bh"><h2>Teams</h2><span>Round {isRound2 ? '2' : '1'} totals</span></div>
        <table>{cols}<thead><tr><th>#</th><th>Team</th><th className="r">Score</th></tr></thead></table>
        {teams.length === 0 ? <p className="empty">{isRound2 ? 'Round 2 starting soon' : 'Team scores will show up here as they are entered.'}</p> : null}
        <div className="pinned"><table>{cols}<tbody>{teamRows(0, 3)}</tbody></table></div>
        <div className="win team-win" ref={paging.viewport}>
          <div className="whole-row-clip" style={{ height: paging.page.height }}>
            <table ref={paging.content} style={{ transform: `translateY(-${paging.page.offset}px)` }}>{cols}<tbody>{teamRows(3)}</tbody></table>
          </div>
        </div>
      </div>
  );
}

function Head({ showTeam }: { showTeam?: boolean }) {
  return (
    <table>
      <Cols showTeam={showTeam} />
      <thead>
        <tr>
          <th>#</th>
          {!showTeam ? <th>Bib</th> : null}
          <th>Name</th>
          <th className="r">Score</th>
        </tr>
      </thead>
    </table>
  );
}

function Cols({ showTeam }: { showTeam?: boolean }) {
  return (
    <colgroup>
      <col className="c-rank" />
      {!showTeam ? <col className="c-bib" /> : null}
      <col />
      <col className="c-score" />
    </colgroup>
  );
}

function ShooterTable({ lines, offset, showTeam, tableRef }: { lines: BigScreenLine[]; offset?: number; showTeam?: boolean; tableRef?: RefObject<HTMLTableElement | null> }) {
  return (
    <table ref={tableRef} style={offset ? { transform: `translateY(${-offset}px)` } : undefined}>
      <Cols showTeam={showTeam} />
      <tbody>
        {lines.map((line, index) =>
          line.kind === 'cut' ? (
            <tr key={`cut-${index}`} className="cutlabel">
              <td colSpan={showTeam ? 3 : 4}>▲ Round 2 qualifiers</td>
            </tr>
          ) : (
            <tr
              key={line.shooter.shooterId}
              className={[line.shooter.top ? 'top' : '', line.cutAfter ? 'cut' : '', line.shooter.position % 2 === 1 ? 'odd' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <td className="rank">{line.shooter.rank}</td>
              {!showTeam ? <td className="bib">{line.shooter.bib ?? ''}</td> : null}
              <td title={[line.shooter.name, line.shooter.bib, line.shooter.teamName].filter(Boolean).join(' · ')}>
                <span className="shooter-name">{line.shooter.name}</span>
                {showTeam ? <small className="shooter-detail">{[line.shooter.bib, line.shooter.teamName].filter(Boolean).join(' · ')}</small> : null}
              </td>
              <td className="score">{line.shooter.score}</td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}
